import { useEffect, useMemo, useState } from "react";
import { LayoutChangeEvent, View } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
	Easing,
	ReduceMotion,
	runOnJS,
	useAnimatedStyle,
	useDerivedValue,
	useSharedValue,
	withSpring,
	withTiming,
} from "react-native-reanimated";
import useStyle from "src/hooks/useStyle";
import { color } from "src/styles";
import useTrunkLeafNavigationRouter from "../hooks/useTrunkLeafNavigationRouter";
import useTrunkLeafNavigationStore from "../hooks/useTrunkLeafNavigationStore";
import { TrunkLeafNavigationProps } from "../types";
import TrunkLeafNavigationLeafScreen from "./TrunkLeafNavigationLeafScreen";
import TrunkLeafNavigationTrunkScreen from "./TrunkLeafNavigationTrunkScreen";

const SWIPE_TIMING_CONFIG: {
	duration?: number;
	reduceMotion?: ReduceMotion;
	easing?: any;
} = { duration: 500, easing: Easing.bezier(0.25, 0.1, 0.25, 1) };

const TrunkLeafNavigationContent = <TProps extends object>({
	renderTrunkScreen,
	renderLeafScreen,
}: TrunkLeafNavigationProps<TProps>) => {
	const [modalWidth, setModalWidth] = useState<number | null>(null);

	const currentScreen = useTrunkLeafNavigationStore((store) => store.currentScreen);
	const currentLeaf = useTrunkLeafNavigationStore((store) => store.currentLeaf);
	const clearLeaf = useTrunkLeafNavigationStore((store) => store.clearLeaf);
	const { theme } = useStyle();

	const router = useTrunkLeafNavigationRouter();

	const maxTranslationLeft = useMemo(() => (modalWidth ? -modalWidth : 0), [modalWidth]);

	const translateX = useSharedValue(0);
	const prevTranslateX = useSharedValue(0);

	useEffect(() => {
		if (currentScreen === "Trunk") {
			translateX.value = withTiming(0, SWIPE_TIMING_CONFIG, () => {
				"worklet";
				runOnJS(clearLeaf)();
			});
		}
		if (currentScreen === "Leaf") {
			translateX.value = withTiming(maxTranslationLeft, SWIPE_TIMING_CONFIG);
		}
	}, [maxTranslationLeft, translateX, currentScreen, clearLeaf]);

	const panGesture = useMemo(() => {
		const navigateToTrunk = () => {
			router?.navigateToTrunk();
		};
		return Gesture.Pan()
			.onStart(() => {
				"worklet";
				prevTranslateX.value = translateX.value;
			})
			.onUpdate((event) => {
				"worklet";
				translateX.value = Math.max(
					Math.min(0, prevTranslateX.value + event.translationX),
					maxTranslationLeft,
				);
			})
			.onEnd((event) => {
				"worklet";
				if (event.translationX + 5 * event.velocityX > 50) {
					translateX.value = withSpring(
						0,
						{
							velocity: event.velocityX,
							damping: 20,
							overshootClamping: true,
						},
						() => {
							"worklet";
							runOnJS(navigateToTrunk)();
						},
					);
				} else {
					translateX.value = withTiming(prevTranslateX.value);
				}
			})
			.enabled(currentScreen === "Leaf");
	}, [translateX, prevTranslateX, maxTranslationLeft, router, currentScreen]);

	const parallaxTranslateX = useDerivedValue(() => {
		return translateX.value / 4;
	}, [translateX]);
	const trunkAnimatedStyle = useAnimatedStyle(
		() => ({
			transform: [{ translateX: parallaxTranslateX.value }],
		}),
		[parallaxTranslateX],
	);
	const leafAnimatedStyle = useAnimatedStyle(
		() => ({
			transform: [{ translateX: translateX.value }],
		}),
		[translateX],
	);

	const onLayout = (event: LayoutChangeEvent) => {
		setModalWidth(event.nativeEvent.layout.width);
	};

	return (
		<View
			style={{
				width: "100%",
				height: "100%",
				overflow: "hidden",
			}}
			onLayout={onLayout}
		>
			{modalWidth && (
				<GestureDetector gesture={panGesture}>
					<View style={{ flexDirection: "row", height: "100%", width: modalWidth * 2 }}>
						<Animated.View
							style={[
								{
									height: "100%",
									width: modalWidth,
									backgroundColor: color.BackgroundModal[theme],
								},
								trunkAnimatedStyle,
							]}
						>
							<TrunkLeafNavigationTrunkScreen renderTrunkScreen={renderTrunkScreen} />
						</Animated.View>
						{!!currentLeaf && (
							<Animated.View
								style={[
									{
										height: "100%",
										width: modalWidth,
										backgroundColor: color.BackgroundModal[theme],
									},
									leafAnimatedStyle,
								]}
							>
								<TrunkLeafNavigationLeafScreen
									renderLeafScreen={renderLeafScreen}
									props={currentLeaf.props}
								/>
							</Animated.View>
						)}
					</View>
				</GestureDetector>
			)}
		</View>
	);
};
export default TrunkLeafNavigationContent;
